import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import union from 'lodash/union';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import StructureModel from '@Components/Results/structure/structure-model.service';
import ShowMoreComponent from '@Components/Results/utils/show-more.component';
import StructureZoomService from '@Services/StructureZoom';
import Structure from '@Components/Results/structure/Structure';
import * as ResultsDataManage from '@Services/ResultsDataManage';

function DocumentSubstancesComponent({
  metadata,
  documentIndex,
  documentRelativeIndex,
  databaseId,
  substancesCount,
  isHit,
  minCount = 9,
  maxCount = 40,
  zoom,
  closeClick,
}) {
  const [substances, setSubstances] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const pendingRequestRef = useRef(null);
  const isMountedRef = useRef(true); // Track if the component is mounted
  const zoomClasses = StructureZoomService.getZoomClasses('citations') || [];
  const zoomClass = zoomClasses[zoom];

  const getSubstancesForCitations = () => {
    const currentCount = substances.length;
    const context = 'substances';
    const nextSubstancesCount = currentCount === 0 ? minCount : maxCount;

    setIsLoading(true);

    const request = ResultsDataManage.getItemsForCitations({
      resultName: metadata.resultName,
      resultIndex: documentIndex,
      resultRelativeIndex: documentRelativeIndex,
      countOfCurrentItems: currentCount,
      totalItems: substancesCount,
      databaseId,
      sort: metadata.sort,
      order: metadata.order,
      context,
      count: nextSubstancesCount,
      hitData: isHit,
    });

    pendingRequestRef.current = request;

    return request
      .then((data) => {
        if (!isMountedRef.current) return; // Prevent state updates if unmounted

        if (!data) {
          Logger.error('No results returned for document substances');
          return;
        }

        setSubstances((prevSubstances) =>
          union(
            prevSubstances,
            // eslint-disable-next-line sonarjs/no-nested-functions
            data.map((substance) => new StructureModel({ ...substance, databaseId }))
          )
        );
        setIsLoading(false);
        // eslint-disable-next-line consistent-return
        return data; // Ensure the data is returned
      })
      .catch((e) => {
        if (isMountedRef.current) {
          Logger.error(e);
          setIsLoading(false);
        }
      });
  };

  const showLessItems = () => {
    setSubstances((prevSubstances) => prevSubstances.slice(0, minCount));
  };

  useEffect(() => {
    isMountedRef.current = true; // Mark as mounted
    getSubstancesForCitations();
    Logger.info('DocumentSubstancesComponent mounted');
    return () => {
      isMountedRef.current = false; // Mark as unmounted
      const cancel = get(pendingRequestRef.current, 'cancel');
      if (isFunction(cancel)) {
        cancel();
      }
      pendingRequestRef.current = null;
    };
  }, []);

  return (
    <div className="result-detail-panel result-detail-panel-substances">
      <div className="result-detail-panel-wrapper" id="substances-wrapper">
        <a
          data-testid="substances-wrapper-close"
          href="null"
          tabIndex="0"
          className="els-btn els-btn-modal-close e2e-close"
          onClick={(e) => {
            e.preventDefault();
            closeClick();
          }}>
          <svg className="els-btn-icon els-btn-icon-tiny">
            <use xlinkHref="#icon-delete" />
          </svg>
        </a>
        <h4>{isHit ? 'Hit Substances' : 'Substances'}</h4>
        <div className="substance-container">
          {substances.map((structure, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className={`substance-item ${zoomClass}`} key={index}>
              <Structure structure={structure} zoom={zoom} metadata={{ ...metadata, fromDocuments: true }} />
            </div>
          ))}
        </div>
        <div className="rx-btn-group--center">
          {!metadata.isViewPreview && substances.length > 0 && (
            <ShowMoreComponent
              idSuffix={`substances-for-citations-${documentIndex}`}
              labelAll={
                substancesCount - substances.length < 50
                  ? `Show All Available ${substancesCount - substances.length}`
                  : `Show next ${maxCount}`
              }
              initial={minCount}
              current={substances.length}
              total={substancesCount}
              container="#substances-wrapper"
              moreHandler={getSubstancesForCitations}
              lessHandler={showLessItems}
              documentResults
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
}

DocumentSubstancesComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  documentIndex: PropTypes.string.isRequired,
  documentRelativeIndex: PropTypes.string,
  databaseId: PropTypes.string.isRequired,
  substancesCount: PropTypes.number.isRequired,
  isHit: PropTypes.bool,
  closeClick: PropTypes.func.isRequired,
  minCount: PropTypes.number,
  maxCount: PropTypes.number,
  zoom: PropTypes.number,
};

export default DocumentSubstancesComponent;
